
import React, { useEffect, useState, useRef } from "react";
import { useQueryClient } from "react-query";
import { useGetCampaignLayerContent, useUpdateCampaignContentSettings, useUpdatePlaylistContentSettings } from "../../../../../../queries";

export const useLayoutScreenContentControls = (screenDetails, onScreenSettingChange) => {
    const queryClient = useQueryClient();
    const updateCampaignContentMutation = useUpdateCampaignContentSettings();
    const updatePlaylistContentMutation = useUpdatePlaylistContentSettings();

    // Use screenDetails.id as areaId
    const { data: content } = useGetCampaignLayerContent(screenDetails?.id, "MAIN");

    // Refs for stable access inside timeout
    const contentRef = useRef(content);
    const optimisticStateRef = useRef({});
    const timerRef = useRef(null);

    // Local optimistic state for UI rendering
    const [optimisticState, setOptimisticState] = useState({});

    // Keep contentRef updated
    useEffect(() => {
        contentRef.current = content;
    }, [content]);

    // Reset optimistic state when screen changes
    useEffect(() => {
        setOptimisticState({});
        optimisticStateRef.current = {};
        if (timerRef.current) clearTimeout(timerRef.current);
    }, [screenDetails?.id]);

    // Compute states
    const hasContent = content && content.length > 0;

    // Check if ALL items match the setting (Derived State)
    // using Number() to handle potential string/number mismatches from API
    const isAllMutedDerived = hasContent && content.every(c => Number(c.is_muted) === 1);
    const isAllFilledDerived = hasContent && content.every(c => !c.maintain_aspect_ratio || Number(c.maintain_aspect_ratio) === 0);

    // Sync Optimistic State with Reality
    useEffect(() => {
        if (!hasContent) return;

        setOptimisticState(prev => {
            const newState = { ...prev };
            let hasChanges = false;

            if (newState.is_muted !== undefined) {
                const expected = newState.is_muted === 1;
                if (isAllMutedDerived === expected) {
                    delete newState.is_muted;
                    hasChanges = true;
                }
            }

            if (newState.maintain_aspect_ratio !== undefined) {
                const expected = newState.maintain_aspect_ratio === 0;
                if (isAllFilledDerived === expected) {
                    delete newState.maintain_aspect_ratio;
                    hasChanges = true;
                }
            }
            return hasChanges ? newState : prev;
        });
    }, [content, isAllMutedDerived, isAllFilledDerived, hasContent]);


    // Determine Checked State (UI)
    const mutedChecked = optimisticState.is_muted !== undefined
        ? optimisticState.is_muted === 1
        : (hasContent ? isAllMutedDerived : !!screenDetails?.settings?.live?.is_muted);

    const filledChecked = optimisticState.maintain_aspect_ratio !== undefined
        ? optimisticState.maintain_aspect_ratio === 0
        : (hasContent ? isAllFilledDerived : !screenDetails?.settings?.live?.maintain_aspect_ratio);


    const executeGlobalUpdate = async () => {
        const currentContent = contentRef.current;
        if (!currentContent || currentContent.length === 0) return;

        const overrides = optimisticStateRef.current;
        const isPlaylist = window.location.pathname.includes("playlist");
        const updateMutation = isPlaylist ? updatePlaylistContentMutation : updateCampaignContentMutation;

        // identify items that need updates based on the Accumulated Overrides
        const itemsToUpdate = currentContent.filter(item => {
            let needsUpdate = false;
            // Note: server data might be string/number mixed, so we use Number() or loose equality if needed
            // But here we set explicit number values in overrides.
            if (overrides.is_muted !== undefined && Number(item.is_muted) !== overrides.is_muted) needsUpdate = true;
            if (overrides.maintain_aspect_ratio !== undefined && Number(item.maintain_aspect_ratio) !== overrides.maintain_aspect_ratio) needsUpdate = true;
            return needsUpdate;
        });

        if (itemsToUpdate.length === 0) return;

        await Promise.all(itemsToUpdate.map(async (item) => {
            const formData = new FormData();

            // Determine final values: prefer override, then item value
            const finalMuted = overrides.is_muted !== undefined ? overrides.is_muted : item.is_muted;
            const finalAspectRatio = overrides.maintain_aspect_ratio !== undefined ? overrides.maintain_aspect_ratio : item.maintain_aspect_ratio;

            const settings = {
                "show_video_screen": item.show_video_screen,
                "is_muted": finalMuted,
                "maintain_aspect_ratio": finalAspectRatio,
                "settings": item.media_settings,
                "is_showed": item.is_showed,
                "downloadable": item.downloadable,
                "show_layer_two": item.show_layer_two,
                "show_layer_one": item.show_layer_one,
                "show_layer_three": item.show_layer_three,
            };

            for (let k in settings) {
                if (k === "settings") {
                    const orderedSettings = {
                        width: item.media_settings?.width || 100,
                        height: item.media_settings?.height || 100,
                        top_margin: item.media_settings?.top_margin || 0,
                        start_margin: item.media_settings?.start_margin || 0,
                    };
                    formData.append("settings", JSON.stringify(orderedSettings));
                } else {
                    formData.append(k, Number(settings[k]));
                }
            }

            if (isPlaylist) {
                formData.append("playlist_media_id", item.playlist_media_id);
            }

            try {
                await updateMutation.mutateAsync({
                    data: formData,
                    id: isPlaylist ? undefined : item.campaign_playlist_media_id,
                    suppressDialog: true
                });
            } catch (error) {
                console.error("Failed to update content item:", item.id, error);
            }
        }));

        // Clear overrides after applying them to prevent affecting individual updates
        optimisticStateRef.current = {};
        setOptimisticState({});

        // After bulk update, invalidate queries to refresh list
        queryClient.invalidateQueries(['getCampaignLayerContent', screenDetails?.id, "MAIN"]);
    };

    const handleUpdate = (key, value) => {
        // 1. Update State & Ref
        setOptimisticState(prev => ({ ...prev, [key]: value }));
        optimisticStateRef.current = { ...optimisticStateRef.current, [key]: value };

        // 2. Trigger parent setting change (The Area Setting itself)
        if (onScreenSettingChange) {
            onScreenSettingChange(key, value);
        }

        // 3. Debounce Global Update
        if (hasContent) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(executeGlobalUpdate, 500); // 500ms debounce
        }
    }

    return {
        mutedChecked,
        filledChecked,
        handleUpdate
    };
};
